import { PaymentTermEditForm } from './EditPaymentTerm';
import { hasUserPermission } from '../utils/userPermissions';
import CreateRecordModal from '../components/CreateRecordModal';
import PermissionGate from '../components/PermissionGate';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { showSuccessToast, showErrorToast } from "../utils/toast";
import { Alert, Button, Card, Table } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus, faTrash, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';

import { API_URL, END_POINT_PAYMENT_TERMS } from '../const';
import { PaymentTermForm } from './CreatePaymentTerm';

const PaymentTerms = () => {
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [creationMessage, setCreationMessage] = useState('');
  const token = sessionStorage.getItem('quotetoken')?.replace(/^"|"$/g, '');

  useEffect(() => {
    let active = true;
    setLoading(true);
    const fetchPaymentTerms = async () => {
      try {
        const response = await axios.get(`${API_URL}${END_POINT_PAYMENT_TERMS}`, {
          headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
        });
        if (active) setPaymentTerms(response.data.data || []);
      } catch (error) {
        if (active) {
          showErrorToast(error.response?.data?.message || 'Please try again.')
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchPaymentTerms();
    return () => { active = false; };
  }, [refreshKey, token]);

  const closeCreateModal = () => {
    if (!isCreating) setShowCreateModal(false);
  };

  const handlePaymentTermCreated = (result) => {
    setShowCreateModal(false);
    setCreationMessage(result.message || 'Payment term created successfully.');
    setRefreshKey(value => value + 1);
  };

  const openEditModal = id => {
    if (!hasUserPermission('payment_terms', 'edit')) return;
    setCreationMessage('');
    setEditingId(id);
  };

  const closeEditModal = () => {
    if (!isUpdating) setEditingId(null);
  };

  const handleUpdated = result => {
    setEditingId(null);
    setCreationMessage(result.message || 'Payment Term updated successfully.');
    setRefreshKey(value => value + 1);
  };

  const handleDelete = async (paymentTerm) => {
    const result = await Swal.fire({
      title: 'Delete payment term?',
      text: `This will delete "${paymentTerm.name}".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}${END_POINT_PAYMENT_TERMS}/${paymentTerm.id}`, {
        headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      });
      setPaymentTerms((current) => current.filter((item) => item.id !== paymentTerm.id));
      showSuccessToast('Payment term deleted successfully.');
    } catch (error) {
      showErrorToast(error.response?.data?.message || 'Please try again.')
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-3">
        <h2 className="page-breadcrumb mb-0">Payment Terms</h2>
        <PermissionGate module="payment_terms" action="add"><Button
          variant="primary"
          className="btn-quote-blue"
          onClick={() => { setCreationMessage(''); setShowCreateModal(true); }}
        >
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Create Payment Term
        </Button></PermissionGate>
      </div>

      {creationMessage && (
        <Alert variant="success" dismissible onClose={() => setCreationMessage('')}>
          {creationMessage}
        </Alert>
      )}

      <Card border="light" className="overflow-hidden border-radius-5 mb-4">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">Loading payment terms...</div>
          ) : paymentTerms.length === 0 ? (
            <div className="text-center py-5">No payment terms found.</div>
          ) : (
            <Table responsive hover className="align-items-center mb-0">
              <thead className="thead-light">
                <tr>
                  <th>#</th>
                  <th>Payment Term</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paymentTerms.map((paymentTerm, index) => (
                  <tr key={paymentTerm.id}>
                    <td>{index + 1}</td>
                    <td className="fw-bold">{paymentTerm.name}</td>
                    <td className="text-end">
                      <PermissionGate module="payment_terms" action="edit"><Button
                        onClick={() => openEditModal(paymentTerm.id)}
                        variant="link"
                        className="p-0 text-success me-3"
                        aria-label={`Edit ${paymentTerm.name}`}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button></PermissionGate>
                      <PermissionGate module="payment_terms" action="delete"><Button
                        variant="link"
                        className="p-0 text-danger"
                        onClick={() => handleDelete(paymentTerm)}
                        aria-label={`Delete ${paymentTerm.name}`}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button></PermissionGate>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      <CreateRecordModal
        show={showCreateModal}
        onHide={closeCreateModal}
        isSubmitting={isCreating}
        titleId="create-payment-term-title"
        title="Create Payment Term"
        description="Define a payment term to use in your quotes."
        icon={faFileInvoiceDollar}
      >
        {showCreateModal && (
          <PaymentTermForm
            onSuccess={handlePaymentTermCreated}
            onCancel={closeCreateModal}
            onSubmittingChange={setIsCreating}
          />
        )}
      </CreateRecordModal>

      <CreateRecordModal
        show={editingId !== null}
        onHide={closeEditModal}
        isSubmitting={isUpdating}
        titleId="edit-payment-term-title"
        title="Edit Payment Term"
        description="Update payment term details."
        icon={faFileInvoiceDollar}
      >
        {editingId !== null && (
          <PaymentTermEditForm key={editingId} paymentTermId={editingId}
            onSuccess={handleUpdated} onCancel={closeEditModal} onSubmittingChange={setIsUpdating} />
        )}
      </CreateRecordModal>
    </>
  );
};

export default PaymentTerms;
